import { Column, Table as TableBase, VirtualTable } from '@devexpress/dx-react-grid';
import { Table } from '@devexpress/dx-react-grid-material-ui';
import { Tooltip } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { SearchDeviceContracts, searchDevices } from '../Async/SearchDevicesAsync';
import { DeviceStatus } from '../Async/ServicesAustraliaModels';
import { PermissionsCheck } from '../Components/Auth/PermissionsCheck';
import { BreadCrumb, BreadCrumbs } from '../Components/BreadCrumbs';
import { Button } from '../Components/Button';
import { DataTable } from '../Components/DataTable/DataTable';
import { PlusIcon, RobotIcon } from '../Components/Icons';
import { LayoutCentral } from '../Components/Layout/LayoutCentral';
import { Toolbar } from '../Components/Layout/Toolbar';
import { LoadingPlaceholder, Placeholder } from '../Components/Placeholder';
import { useAsync } from '../Hooks/useAsync';
import { usePermissions } from '../Hooks/usePermissions';
import { DateExpiration } from '../RegisterDeviceWizard/DateExpiration';
import { RegisterDeviceWizard } from '../RegisterDeviceWizard/RegisterDeviceWizard';
import { routeForDevices, routeForNewDevice } from '../Routes';
import { ServicesAustraliaPermissions } from '../ServicesAustraliaPermissions';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { useHttpConfig } from '../Shared/HttpRequestExecutor';
import { Nullable } from '../Shared/Types';
import { white } from '../Styles/Colours';
import styles from './DeviceList.module.scss';
import { DeviceTokenTester } from './DeviceTokenTester';
import { OPVCheck } from './OPVCheck';
import { UpdateDevice } from './UpdateDevice';

type Props = {
    status: Nullable<DeviceStatus>;
};

export function DeviceStatusChip(props: Props): JSX.Element {
    return (
        <div className={props.status === DeviceStatus.Registered ? styles.chipGreen : styles.chipRed}>
            {props.status}
        </div>
    );
}

const columnExtensions: VirtualTable.ColumnExtension[] = [
    {
        columnName: 'device',
        width: 160
    },
    {
        columnName: 'status',
        width: 120
    },
    {
        columnName: 'expiresOn',
        width: 170
    }
];

const columnConfig: Column[] = [
    {
        name: 'authoringSystem',
        title: 'Device'
    },
    {
        name: 'device',
        title: 'PRODA'
    },
    {
        name: 'healthSystems',
        title: 'Health Systems'
    },
    {
        name: 'customer',
        title: 'Customer'
    },
    {
        name: 'expiresOn',
        title: 'Expiry'
    },
    {
        name: 'status',
        title: 'Status'
    },
    {
        name: 'errors',
        title: 'Errors'
    },
    {
        name: 'actions',
        title: 'Actions'
    }
];

const TableCellText = (props: { text: Nullable<string> }) => {
    if (props.text === null) return null;

    return (
        <div className={styles.tableCellLine}>
            <Tooltip title={props.text}>
                <span className={styles.text}>{props.text}</span>
            </Tooltip>
        </div>
    );
};

const createCustomCell = (config: { deviceUpdated: () => void; }) => (cellProps: TableBase.DataCellProps) => {
    const row: SearchDeviceContracts.Response.DevicesSearchResult = cellProps.row;
    let content = null;

    if (cellProps.column.name === 'authoringSystem') {
        content = (
            <>
                <TableCellText text={row.authoringSystem} />
                <TableCellText text={row.authoringSystemForeignKey} />
            </>
        );
    } else if (cellProps.column.name === 'device') {
        content = (
            <>
                <TableCellText text={row.prodaDeviceName} />
                <TableCellText text={row.prodaOrganisationIdentifier} />
            </>
        );
    } else if (cellProps.column.name === 'healthSystems') {
        content = (
            <>
                <TableCellText text={row.applicationClientIdentifier} />
                <TableCellText text={`${row.applicationName} / ${row.prodaClientIdentifier}`} />
            </>
        );
    } else if (cellProps.column.name === 'customer') {
        content = (
            <>
                <TableCellText text={`${row.customer.name} / ${row.customer.authoringSystem}`} />
                <TableCellText text={row.customer.authoringSystemForeignKey} />
            </>
        );
    } else if (cellProps.column.name === 'status') {
        content = <DeviceStatusChip status={row.status} />;
    } else if (cellProps.column.name === 'expiresOn') {
        content = row.expiresOn ? (
            <>
                <DateExpiration
                    toDate={moment(row.expiresOn, ValidDateFormats.NodeDateTimeTimezone)}
                    fromDate={moment()}
                    testId={'device-expiration'}
                />
            </>
        ) : (
            '-'
        );
    } else if (cellProps.column.name === 'errors') {
        content =
            row.status === DeviceStatus.Error && row.errors.length ? (
                <TableCellText text={row.errors[0].message} />
            ) : (
                '-'
            );
    } else if (cellProps.column.name === 'actions') {
        content = (
            <span>
                <DeviceTokenTester
                    authoringSystem={row.authoringSystem}
                    authoringSystemForeignKey={row.authoringSystemForeignKey}
                />
                <OPVCheck device={row} />
                <UpdateDevice
                    authoringSystem={row.authoringSystem}
                    authoringSystemForeignKey={row.authoringSystemForeignKey}
                    prodaClientID={row.applicationClientIdentifier}
                    applicationName={row.applicationName}
                    deviceUpdated={config.deviceUpdated}
                />
            </span>
        );
    }

    return (
        <Table.Cell {...cellProps} className={styles.cell}>
            {content}
        </Table.Cell>
    );
};

export type DeviceListFilters = {
    devices?: {
        authoringSystem: string;
        authoringSystemForeignKey: string;
    }[];
    deviceExpiresWithin?: string;
    keyExpiresWithin?: string;
    statuses?: DeviceStatus[];
};

export function DeviceList(props: RouteComponentProps): React.ReactElement {
    const history = useHistory();
    const location = useLocation<DeviceListFilters>();
    const limit = 150;
    const httpConfig = useHttpConfig();
    const [targetDevice, setTargetDevice] =
        useState<Nullable<SearchDeviceContracts.Response.DevicesSearchResult>>(null);

    const devicesAsync = useAsync({
        defaultValue: [],
        httpConfig,
        getApi: searchDevices
    });

    const expiryFilter = location.state?.deviceExpiresWithin ?? null;

    const searchPayload: SearchDeviceContracts.Request.SearchDevices = {
        devices: location.state?.devices ?? [],
        deviceExpiresWithin: location.state?.deviceExpiresWithin ?? null,
        keyExpiresWithin: location.state?.keyExpiresWithin ?? null,
        statuses: location.state?.statuses ?? []
    };

    function refreshDevices() {
        devicesAsync.getApi(searchPayload, limit + 1);
    }

    useEffect(() => {
        refreshDevices();
    }, [expiryFilter]);

    const customCellWithRefresh = createCustomCell({ deviceUpdated: refreshDevices });

    const openNewDeviceDialog = () => props.history.push(routeForNewDevice);

    const onRowSelection = async (device: SearchDeviceContracts.Response.DevicesSearchResult) => {
        setTargetDevice(device);
    };

    const permissions = usePermissions({
        registerDevice: ServicesAustraliaPermissions.RegisterDevice
    });

    function getResults() {
        if (devicesAsync.loadCount === 0 || devicesAsync.data === null)
            return (
                <LayoutCentral>
                    <LoadingPlaceholder />
                </LayoutCentral>
            );

        if (devicesAsync.loadCount > 0 && devicesAsync.data.length <= 0) {
            if (!permissions.registerDevice)
                return <Placeholder icon={<RobotIcon />} infoText={'Whoops! We could not find any records.'} />;
            return (
                <Placeholder
                    icon={<RobotIcon />}
                    actionText={'Register Device'}
                    action={openNewDeviceDialog}
                    infoText={'Whoops! We could not find any records. Would you like to:'}
                />
            );
        }

        return (
            <DataTable<SearchDeviceContracts.Response.DevicesSearchResult>
                data={devicesAsync.data}
                columns={columnConfig}
                columnExtensions={columnExtensions}
                limit={limit}
                rowSelection={{
                    selectedIds: [],
                    onSelect: onRowSelection
                }}
                cellComponent={customCellWithRefresh}
                getRowId={data => data.id}
                testId='user-table'
            />
        );
    }

    const results = getResults();

    function returnToDeviceList() {
        history.push(routeForDevices);
        refreshDevices();
    }

    function closeEditDeviceWizard() {
        setTargetDevice(null);
        refreshDevices();
    }

    const crumbs: BreadCrumb[] = [{ route: '/devices', title: 'Devices' }];

    return (
        <Grid container direction='column' spacing={0}>
            <Grid item>
                <Toolbar
                    fullPadding
                    leftContent={<BreadCrumbs trail={crumbs} />}
                    rightContent={
                        <PermissionsCheck permission={ServicesAustraliaPermissions.RegisterDevice}>
                            <Button
                                testId='create-device'
                                onClick={openNewDeviceDialog}
                                startIcon={<PlusIcon size='24' color={white} />}
                            >
                                Register device
                            </Button>
                        </PermissionsCheck>
                    }
                />
            </Grid>
            <Grid item xs className={styles.flex}>
                <div className={styles.dataTableContainer}>{results}</div>
                <Route path={routeForNewDevice}>
                    <RegisterDeviceWizard onClose={returnToDeviceList} />
                </Route>
                {targetDevice ? (
                    <RegisterDeviceWizard onClose={closeEditDeviceWizard} targetDevice={targetDevice} />
                ) : null}
            </Grid>
        </Grid>
    );
}
